'use client';

import { Trash02, UploadCloud02 } from '@ethsign/icons';
import { Button, toast } from '@ethsign/ui';
import classNames from 'classnames';
import RcUpload from 'rc-upload';
import { UploadProps } from 'rc-upload/es/interface';
import { FC, ReactNode, useEffect, useState } from 'react';

import { DeleteIcon } from '@/components';
import { parseFileSize } from '@/utils';

import ProcessBar from './ProcessBar';

interface Props {
  getFile: (file: File) => void;
  accept?: string;
  description?: string;
  className?: string;
  hideProcess?: boolean;
  trigger?: ReactNode;
  maxSize?: number;
}

export const Upload: FC<Props> = ({
  getFile,
  accept = '.pdf',
  description = 'Supports PDF file: Max 5MB',
  className,
  trigger,
  maxSize = 5
}) => {
  const props = {
    type: 'drag',
    multiple: false,
    accept,
    beforeUpload(file: any) {
      if (file.size > maxSize * 1024 * 1024) {
        setIsFileHuge(true);
        return false;
      }
      setFile(file);
      return false;
    }
  } as any;
  const [file, setFile] = useState<File>();
  const [isFileHuge, setIsFileHuge] = useState(false);
  const progress = 100;
  useEffect(() => {
    if (file) {
      getFile(file);
    }
  }, [file, progress]);
  return (
    <>
      <div className={classNames(['border rounded-2xl border-gray-300 border-dashed h-80 bg-white'], className)}>
        <RcUpload {...props}>
          {trigger || (
            <div className="h-full w-full flex justify-center items-center flex-col">
              <img
                src="https://sign-public-cdn.s3.us-east-1.amazonaws.com/signature-next/upload_240723072539.webp"
                width={78}
                height={64}
              />
              <div className="text-md font-medium text-gray-900 mt-5">
                Drag & drop file or <span className="text-primary-900">Browse</span>
              </div>
              <div className=" text-xs font-normal text-gray-500 mt-6">{description}</div>
              {isFileHuge && (
                <div className="mt-6 text-[#EC3435] text-md">
                  File is too large. Maximum file size allowed is {maxSize}MB.
                </div>
              )}
            </div>
          )}
        </RcUpload>
      </div>
      {file && (
        <>
          <div className="text-sm font-medium text-gray-900 mt-8"> Uploaded</div>
          <div
            className={`relative flex flex-col mt-2 h-10 border rounded ${
              progress === 100 ? 'border-slime-500' : 'border-gray-300'
            }`}
          >
            <div className="h-full flex flex-row items-center">
              <div className="flex flex-row justify-between flex-1 pl-4 items-center text-xs text-gray-900">
                <span>{file?.name}</span>
                <span>{parseFileSize(file!.size)}</span>
              </div>
              <div className="w-10 ml-4 flex flex-row justify-center items-center" onClick={() => setFile(undefined)}>
                <DeleteIcon width={16} height={16}></DeleteIcon>
              </div>
            </div>
            {progress < 100 && (
              <div className="absolute w-full bottom-0">
                <ProcessBar value={progress} />
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
};

export const ImageUpload = ({
  onChange,
  className,
  trigger,
  value,
  maxSize = 5
}: {
  onChange: (url: string) => void;
  className?: string;
  trigger?: ReactNode;
  value?: string;
  maxSize?: number;
}) => {
  const [loading, setLoading] = useState(false);
  const props: UploadProps = {
    action: '/api/common/upload2public',
    type: 'drag',
    multiple: false,
    accept: 'image/png, image/jpeg',
    onStart: () => {
      setLoading(true);
    },
    onError: (err: any) => {
      console.log(err);
      setLoading(false);
    },
    onSuccess: (response: any) => {
      const fileUrl = response.data.fileUrl;
      onChange?.(fileUrl);
      setUrl(fileUrl);
      setLoading(false);
    },
    beforeUpload(file: any) {
      if (file.size > maxSize * 1024 * 1024) {
        toast({
          title: 'File is too large.',
          description: `Maximum file size allowed is ${maxSize}MB.`,
          variant: 'error'
        });
        return false;
      }
      // setFile(file);
    }
  } as any;
  const [url, setUrl] = useState<string>(value || '');

  if (url) {
    return (
      <div className={`relative border rounded-md w-32 overflow-hidden`}>
        <img src={url} className={'w-full h-32 object-cover'} alt="" />
        <div className="absolute top-1 right-1 cursor-pointer" onClick={() => setUrl('')}>
          <Trash02 width={16} height={16} color={'#ff4d4f'}></Trash02>
        </div>
      </div>
    );
  }
  return (
    <>
      <div className={classNames(['border rounded-2xl border-gray-300 border-dashed h-80 bg-white'], className)}>
        <RcUpload {...props}>
          {trigger || (
            <Button variant={'outline'} loading={loading}>
              <UploadCloud02 size={16} color={'#344054'} className={'mr-2'} /> Upload Image
            </Button>
          )}
        </RcUpload>
      </div>
    </>
  );
};
